export type AuthOperation =
  | 'sign-in'
  | 'sign-up'
  | 'resend-confirmation'
  | 'request-reset'
  | 'update-password'
  | 'checkout'
  | 'callback';

type AuthErrorLike = {
  code?: unknown;
  message?: unknown;
  status?: unknown;
};

const AUTHENTICATED_ROUTE_PREFIXES = [
  '/account',
  '/chat',
  '/claim-charts',
  '/tabular-reviews',
  '/workflows',
  '/research',
  '/document-editor',
  '/matters',
] as const;

/**
 * Accept only known authenticated product paths. This keeps the post-auth
 * `next` parameter useful without turning it into an open redirect or a loop
 * back through the public auth routes.
 */
export function safeAuthNext(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null;
  for (const character of raw) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 31 || codePoint === 127) return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(raw, 'https://auth.local');
  } catch {
    return null;
  }
  if (parsed.origin !== 'https://auth.local') return null;

  const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  return AUTHENTICATED_ROUTE_PREFIXES.some(
    (prefix) => parsed.pathname === prefix || parsed.pathname.startsWith(`${prefix}/`),
  )
    ? path
    : null;
}

export function buildUnauthorizedAuthPath(location: {
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  const next = safeAuthNext(
    `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`,
  );
  const params = new URLSearchParams({ error: 'unauthorized' });
  if (next) params.set('next', next);
  return `/auth?${params.toString()}`;
}

export function buildAuthCallbackUrl(
  origin: string,
  options: {
    flow: 'confirmation' | 'recovery';
    plan?: 'free' | 'pro';
    next?: string | null;
  },
): string {
  const url = new URL('/auth/callback', origin);
  url.searchParams.set('flow', options.flow);
  if (options.flow === 'confirmation') {
    url.searchParams.set('plan', options.plan === 'pro' ? 'pro' : 'free');
  }
  const next = safeAuthNext(options.next);
  if (next) url.searchParams.set('next', next);
  return url.toString();
}

export function buildAuthEntryPath(options: {
  mode: 'signin' | 'signup' | 'forgot' | 'reset';
  error?: 'confirmation_failed' | 'recovery_failed' | 'unauthorized';
  plan?: 'free' | 'pro';
  next?: string | null;
}): string {
  const params = new URLSearchParams({ mode: options.mode });
  if (options.error) params.set('error', options.error);
  if (options.plan) params.set('plan', options.plan);
  const next = safeAuthNext(options.next);
  if (next) params.set('next', next);
  return `/auth?${params.toString()}`;
}

/**
 * Provider diagnostics can contain tenant, policy, or account-existence
 * detail. Only recognize a small set of safe user-action categories and use
 * operation-specific fallback copy for everything else.
 */
export function authFailureMessage(operation: AuthOperation, error: unknown): string {
  const candidate =
    error && typeof error === 'object' ? (error as AuthErrorLike) : {};
  const code = typeof candidate.code === 'string' ? candidate.code.toLowerCase() : '';
  const message =
    typeof candidate.message === 'string' ? candidate.message.toLowerCase() : '';
  const status = typeof candidate.status === 'number' ? candidate.status : null;
  const fingerprint = `${code} ${message}`;

  if (
    operation === 'sign-in'
    && (code === 'email_not_confirmed' || fingerprint.includes('email not confirmed'))
  ) {
    return 'Confirm your email before signing in, then try again.';
  }
  if (fingerprint.includes('captcha') || fingerprint.includes('turnstile')) {
    return 'Security verification expired or failed. Complete it again and retry.';
  }
  if (
    status === 429
    || fingerprint.includes('rate limit')
    || fingerprint.includes('too many requests')
  ) {
    return 'Too many attempts were made. Wait a moment and try again.';
  }
  if (
    operation === 'sign-in'
    && (
      code === 'invalid_credentials'
      || fingerprint.includes('invalid login credentials')
    )
  ) {
    return 'The email or password is incorrect. Check both fields and try again.';
  }

  switch (operation) {
    case 'sign-in':
      return 'Sign in could not be completed. Check your connection and try again.';
    case 'sign-up':
      return 'The account could not be created. Try signing in or reset your password.';
    case 'resend-confirmation':
      return 'The confirmation email could not be resent. Check your connection and try again.';
    case 'request-reset':
      return 'The reset email could not be sent. Check your connection and try again.';
    case 'update-password':
      return 'Your password could not be updated. Request a new reset link and try again.';
    case 'checkout':
      return 'Your account is ready, but checkout could not start. Continue to your account and try again.';
    case 'callback':
      return 'The secure link could not be completed. It may be expired or already used.';
  }
}
